/**
 * Hybrid Collection Filtering System
 * - Intelligently switches between native Shopify filtering and legacy tag-based filtering
 * - Preserves existing custom logic (model deduplication, special rules)
 * - Provides seamless transition as metafield data becomes available
 * - Fully self-contained with all filtering functionality
 */

class HybridCollectionFilters {
  constructor() {
    // Determine filtering mode
    this.filteringMode = window.filteringMode || 'tag-based';
    this.metafieldCoverage = window.metafieldCoverage || 0;
    
    // Configuration
    this.storefrontAccessToken = '76249782dd572514a4ca845b94d77d7d';
    this.shopDomain = window.Shopify?.shop || window.location.hostname;
    this.collectionHandle = document.querySelector('.collection-filters')?.dataset.collectionHandle;
    
    // Elements
    this.filterCheckboxes = document.querySelectorAll('.filter-checkbox');
    this.clearAllButton = document.getElementById('clear-all-filters');
    this.activeFiltersContainer = document.getElementById('active-filter-tags');
    this.productGrid = document.querySelector('.product-grid');
    this.loadingElement = document.querySelector('.collection-loading');
    this.sortSelect = document.querySelector('#SortBy');
    
    // Price filter elements
    this.priceSlider = null;
    this.priceMinInput = document.getElementById('price-min-input');
    this.priceMaxInput = document.getElementById('price-max-input');
    this.applyFiltersButton = document.getElementById('apply-filters-button');
    
    // State
    this.activeFilters = new Map();
    this.productTypeFilters = new Set();
    this.isLoading = false;
    this.priceRange = { min: 0, max: 1000 };
    this.isPriceFilterActive = false;
    this.initialPriceRange = { min: 0, max: 1000 };
    this.filterTimeout = null;
    
    // Model filtering
    this.showUniqueModelsOnly = document.querySelector('#main-collection-product-grid')?.dataset.uniqueModels === 'true';
    this.modelNameWordCount = parseInt(document.querySelector('#main-collection-product-grid')?.dataset.modelNameWordCount || '2');

    // Pagination state
    this.currentPage = 1;
    this.totalPages = 1;
    this.infiniteScrollObserver = null;
    this.loadMoreTrigger = null;
    this.isLoadingMore = false;
    this.hasMorePages = true;
    this.totalProductCount = 0;
    
    // Initialize appropriate filtering system
    if (this.filteringMode === 'native') {
      this.initNativeFiltering();
    } else {
      this.initTagBasedFiltering();
    }
    
    // Common initialization
    this.initCommonFeatures();
    
    console.log(`Hybrid filtering initialized: ${this.filteringMode} mode (${this.metafieldCoverage}% metafield coverage)`);
  }
  
  initNativeFiltering() {
    // Initialize native Shopify filtering with enhancements
    this.nativeFilters = new NativeFilterEnhancer();
    console.log('Native filtering mode: using Shopify facets with enhancements');
  }
  
  initTagBasedFiltering() {
    // Initialize tag-based filtering
    console.log('Tag-based filtering mode: using custom tag-based system');
    
    if (!this.collectionHandle) return;
    
    // Bind tag-based filter events
    this.filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', this.handleFilterChange.bind(this));
    });
    
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', this.clearAllFilters.bind(this));
    }

    // Sort select change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', this.handleSortChange.bind(this));
    }

    // Initialize price filter for tag-based mode
    this.initPriceFilter();
    
    // Initialize filter tabs
    this.initFilterTabs();
    
    // Load initial state from URL
    this.initFromUrl();
    
    // Initialize infinite scroll for tag-based mode
    this.setupTagBasedInfiniteScroll();
  }
  
  initCommonFeatures() {
    // Model deduplication (preserved regardless of filtering mode)
    this.initModelDeduplication();
    
    // Special business rules
    this.initBusinessRules();
    
    // UI enhancements
    this.initUIEnhancements();
  }

  // Tag-based filtering methods
  handleFilterChange(event) {
    const checkbox = event.target;
    const category = checkbox.dataset.category;
    const value = checkbox.dataset.value;
    
    console.log('Tag filter changed:', category, value);
    
    if (!category || !value) return;
    
    // Find parent label and add/remove active class
    const label = checkbox.closest('.filter-option-label');

    // Handle product type filter
    if (category === 'product-type') {
      if (checkbox.checked) {
        if (label) label.classList.add('active');
        this.productTypeFilters.add(value);
      } else {
        if (label) label.classList.remove('active');
        this.productTypeFilters.delete(value);
      }
      
      this.updateActiveFiltersUI();
      this.updateURL();
      
      // Add debounce for dynamic filtering
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.fetchFilteredProducts();
      }, 300);
      return;
    }
    
    // Update active filters
    if (checkbox.checked) {
      // Add active class
      if (label) label.classList.add('active');
      
      // Create category Set if it doesn't exist
      if (!this.activeFilters.has(category)) {
        this.activeFilters.set(category, new Set());
      }
      this.activeFilters.get(category).add(value);
    } else {
      // Remove active class
      if (label) label.classList.remove('active');
      
      if (this.activeFilters.has(category)) {
        this.activeFilters.get(category).delete(value);
        if (this.activeFilters.get(category).size === 0) {
          this.activeFilters.delete(category);
        }
      }
    }
    
    this.updateActiveFiltersUI();
    this.updateURL();
    
    // Add debounce for dynamic filtering
    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.fetchFilteredProducts();
    }, 300);
  }
  
  handleSortChange(event) {
    const sortValue = event.target.value;
    
    // Update URL with sort parameter (remove if 'manual' for default Shopify order)
    const url = new URL(window.location.href);
    if (sortValue === 'manual') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortValue);
    }
    window.history.replaceState({}, '', url);
    
    // Fetch products with current filters and new sort
    this.fetchFilteredProducts();
  }

  clearAllFilters() {
    // Uncheck all checkboxes
    this.filterCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
      const label = checkbox.closest('.filter-option-label');
      if (label) label.classList.remove('active');
    });
    
    // Clear active filters and product type filters
    this.activeFilters.clear();
    this.productTypeFilters.clear();
    
    // Clear price filter
    this.isPriceFilterActive = false;
    
    // Reset price slider if it exists
    if (this.priceSlider && this.priceMinInput && this.priceMaxInput) {
      this.priceSlider.set([this.initialPriceRange.min, this.initialPriceRange.max]);
      this.priceMinInput.value = this.initialPriceRange.min;
      this.priceMaxInput.value = this.initialPriceRange.max;
      this.priceRange = { ...this.initialPriceRange };
    }
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }

  async fetchFilteredProducts() {
    if (!this.collectionHandle || this.filteringMode === 'native') return;
    
    this.currentPage = 1;
    this.showLoading();
    
    try {
      // Build URL for collection with filter parameters
      let url = `/collections/${this.collectionHandle}`;
      
      // Add tag-based filter parameters
      if (this.hasActiveFilters()) {
        const tagConstraints = [];
        
        this.activeFilters.forEach((values, category) => {
          // Skip availability filter as it's applied client-side
          if (category !== 'availability') {
            values.forEach(value => {
              // For shape filter, use the original tag value if available
              //replace value with spaces to hyphens
              value = value.replace(/\s+/g, '-').toLowerCase();
              if (category === 'shape') {
                const checkbox = document.querySelector(`.filter-checkbox[data-category="shape"][data-value="${value}"]`);
                if (checkbox && checkbox.dataset.originalValue) {
                  tagConstraints.push(`filter-${category}-${checkbox.dataset.originalValue}`);
                } else {
                  tagConstraints.push(`filter-${category}-${value}`);
                }
              } else {
                tagConstraints.push(`filter-${category}-${value}`);
              }
            });
          }
        });
        
        if (tagConstraints.length > 0) {
          url += `/${tagConstraints.join('+')}`;
        }
      }
      
      // Add view parameter for JSON response
      url += '?view=ajax';
      
      // Add sort parameter if present (exclude 'manual' as it's Shopify's default)
      if (this.sortSelect?.value && this.sortSelect.value !== 'manual') {
        url += `&sort_by=${this.sortSelect.value}`;
      }
      
      // Fetch filtered collection HTML
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const html = await response.text();
      
      // Update product grid with filtered results
      this.updateProductGrid(html);
      
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      this.hideLoading();
    }
  }

  // Helper methods for tag-based filtering
  hasActiveFilters() {
    return this.activeFilters.size > 0 || this.productTypeFilters.size > 0 || this.isPriceFilterActive;
  }

  updateProductGrid(html) {
    if (!this.productGrid) return;
    
    // Clear existing products except loading element
    const loadingElement = this.productGrid.querySelector('.collection-loading');
    this.productGrid.innerHTML = '';
    if (loadingElement) {
      this.productGrid.appendChild(loadingElement);
    }
    
    // Add new products
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Extract product elements
    let productElements = tempDiv.querySelectorAll('.card-product-wrapper');
    
    // Apply client-side filters
    productElements = this.applyClientSideFilters(Array.from(productElements));
    
    if (productElements.length === 0) {
      // Show no results message
      const noResults = document.createElement('div');
      noResults.className = 'collection-no-results';
      noResults.innerHTML = `
        <h2 class="h3">No products found</h2>
        <p>Try adjusting your filters or <a class="link link-underline-start" href="/collections/${this.collectionHandle}">view all products</a>.</p>
      `;
      this.productGrid.appendChild(noResults);
      this.hasMorePages = false;
    } else {
      // Add filtered products to grid
      productElements.forEach(product => {
        this.productGrid.appendChild(product);
      });
      
      // Parse pagination info from HTML to determine if there are more pages
      this.parseTagBasedPaginationData(tempDiv);
    }
    
    this.hideLoading();
    
    // Update product count
    this.updateProductCount(productElements.length);
    
    // Setup infinite scroll trigger for tag-based mode
    this.setupTagBasedScrollTrigger();
    
    // Auto-fill page if filtered results are below threshold
    this.autoFillPageIfNeeded();
    
    // Dispatch custom event for other components
    document.dispatchEvent(new CustomEvent('collection:filtered', {
      detail: { count: productElements.length }
    }));
  }

  applyClientSideFilters(productElements) {
    // Apply availability filter
    if (this.activeFilters.has('availability')) {
      const availabilityFilters = this.activeFilters.get('availability');
      
      productElements = productElements.filter(element => {
        const isInStock = !element.classList.contains('sold-out');
        
        if (availabilityFilters.has('in-stock') && availabilityFilters.has('sold-out')) {
          return true; // Show all if both are selected
        } else if (availabilityFilters.has('in-stock')) {
          return isInStock;
        } else if (availabilityFilters.has('sold-out')) {
          return !isInStock;
        }
        return true;
      });
    }
    
    // Apply product type filters
    if (this.productTypeFilters.size > 0) {
      productElements = productElements.filter(element => {
        const productType = element.dataset.productType?.toLowerCase() || '';
        return Array.from(this.productTypeFilters).some(type => 
          productType.includes(type.toLowerCase())
        );
      });
    }
    
    // Apply model deduplication if enabled
    if (this.showUniqueModelsOnly) {
      productElements = this.applyModelDeduplicationToElements(productElements, this.modelNameWordCount);
    }
    
    return productElements;
  }

  updateActiveFiltersUI() {
    if (!this.activeFiltersContainer) return;
    
    this.activeFiltersContainer.innerHTML = '';
    
    // Create a fragment for better performance
    const fragment = document.createDocumentFragment();
    
    // Add a tag for each active filter
    this.activeFilters.forEach((values, category) => {
      values.forEach(value => {
        const filterTag = document.createElement('a');
        filterTag.className = 'active-filter-tag';
        filterTag.classList.add('button', 'button--secondary');
        filterTag.href = 'javascript:void(0)';
        filterTag.dataset.category = category;
        filterTag.dataset.value = value;
        
        // Create display text
        const categoryText = this.formatCategoryName(category);
        const valueText = this.formatValueName(value);
        filterTag.innerHTML = `
          ${categoryText}: ${valueText}
          <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
            <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="inherit"></path>
          </svg>
        `;
        
        // Add click handler to remove filter
        filterTag.addEventListener('click', (e) => {
          e.preventDefault();
          this.removeFilter(category, value);
        });
        
        fragment.appendChild(filterTag);
      });
    });
    
    // Add product type filters
    this.productTypeFilters.forEach(type => {
      const filterTag = document.createElement('a');
      filterTag.className = 'active-filter-tag';
      filterTag.classList.add('button', 'button--secondary');
      filterTag.href = 'javascript:void(0)';
      filterTag.dataset.category = 'product-type';
      filterTag.dataset.value = type;
      
      filterTag.innerHTML = `
        Product Type: ${this.formatValueName(type)}
        <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
          <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="inherit"></path>
        </svg>
      `;
      
      filterTag.addEventListener('click', (e) => {
        e.preventDefault();
        this.removeProductTypeFilter(type);
      });
      
      fragment.appendChild(filterTag);
    });
    
    this.activeFiltersContainer.appendChild(fragment);
    
    // Show/hide clear all button
    if (this.clearAllButton) {
      this.clearAllButton.style.display = this.hasActiveFilters() ? 'inline-block' : 'none';
    }
  }

  formatCategoryName(category) {
    const categoryMap = {
      'color': 'Color',
      'shape': 'Shape',
      'material': 'Material',
      'gender': 'Gender',
      'age-group': 'Age Group',
      'case-size': 'Case Size',
      'strap-color': 'Strap Color',
      'strap-material': 'Strap Material',
      'availability': 'Availability'
    };
    return categoryMap[category] || category;
  }

  formatValueName(value) {
    // Capitalize first letter and replace hyphens with spaces
    return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
  }

  removeFilter(category, value) {
    // Find and uncheck the corresponding checkbox
    const checkbox = document.querySelector(`.filter-checkbox[data-category="${category}"][data-value="${value}"]`);
    if (checkbox) {
      checkbox.checked = false;
      const label = checkbox.closest('.filter-option-label');
      if (label) label.classList.remove('active');
    }
    
    // Remove from active filters
    if (this.activeFilters.has(category)) {
      this.activeFilters.get(category).delete(value);
      if (this.activeFilters.get(category).size === 0) {
        this.activeFilters.delete(category);
      }
    }
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }

  removeProductTypeFilter(type) {
    // Find and uncheck the corresponding checkbox
    const checkbox = document.querySelector(`.filter-checkbox[data-category="product-type"][data-value="${type}"]`);
    if (checkbox) {
      checkbox.checked = false;
      const label = checkbox.closest('.filter-option-label');
      if (label) label.classList.remove('active');
    }
    
    // Remove from product type filters
    this.productTypeFilters.delete(type);
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }

  updateURL() {
    const url = new URL(window.location.href);
    
    // Clear existing filter parameters
    url.searchParams.delete('filter');
    url.searchParams.delete('product_type');
    
    // Add active filters to URL
    const filterParams = [];
    this.activeFilters.forEach((values, category) => {
      values.forEach(value => {
        filterParams.push(`${category}:${value}`);
      });
    });
    
    // Add product type filters
    this.productTypeFilters.forEach(type => {
      filterParams.push(`product_type:${type}`);
    });
    
    if (filterParams.length > 0) {
      url.searchParams.set('filter', filterParams.join(','));
    }
    
    window.history.replaceState({}, '', url);
  }

  initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const filterParam = params.get('filter');
    
    if (!filterParam) return;
    
    const filters = filterParam.split(',');
    filters.forEach(filter => {
      const [category, value] = filter.split(':');
      if (category && value) {
        if (category === 'product_type') {
          this.productTypeFilters.add(value);
          // Check corresponding checkbox
          const checkbox = document.querySelector(`.filter-checkbox[data-category="product-type"][data-value="${value}"]`);
          if (checkbox) {
            checkbox.checked = true;
            const label = checkbox.closest('.filter-option-label');
            if (label) label.classList.add('active');
          }
        } else {
          // Regular filter
          if (!this.activeFilters.has(category)) {
            this.activeFilters.set(category, new Set());
          }
          this.activeFilters.get(category).add(value);
          
          // Check corresponding checkbox
          const checkbox = document.querySelector(`.filter-checkbox[data-category="${category}"][data-value="${value}"]`);
          if (checkbox) {
            checkbox.checked = true;
            const label = checkbox.closest('.filter-option-label');
            if (label) label.classList.add('active');
          }
        }
      }
    });
    
    this.updateActiveFiltersUI();
    this.fetchFilteredProducts();
  }

  initFilterTabs() {
    // Initialize collapsible filter sections
    const filterDropdowns = document.querySelectorAll('.filter-dropdown');
    
    filterDropdowns.forEach(dropdown => {
      const heading = dropdown.querySelector('.filter-dropdown-heading');
      const content = dropdown.querySelector('.filter-dropdown-content');
      const icon = heading?.querySelector('.filter-dropdown-icon svg');
      
      if (heading && content) {
        heading.addEventListener('click', () => {
          const isOpen = dropdown.classList.contains('open');
          
          // Close all other dropdowns (accordion behavior)
          filterDropdowns.forEach(other => {
            if (other !== dropdown) {
              other.classList.remove('open');
              const otherIcon = other.querySelector('.filter-dropdown-icon svg');
              if (otherIcon) {
                otherIcon.style.transform = 'rotate(0deg)';
              }
            }
          });
          
          // Toggle current dropdown
          dropdown.classList.toggle('open');
          
          // Rotate icon properly (90 degrees makes + into -, not 45 degrees)
          if (icon) {
            icon.style.transform = dropdown.classList.contains('open') ? 'rotate(90deg)' : 'rotate(0deg)';
          }
        });
      }
    });
  }

  initPriceFilter() {
    // Price filter implementation would go here
    // For now, keeping it simple
    console.log('Price filter initialization for tag-based mode');
  }

  showLoading() {
    this.isLoading = true;
    if (this.loadingElement) {
      this.loadingElement.style.display = 'block';
    }
    if (this.productGrid) {
      this.productGrid.classList.add('loading');
    }
  }

  hideLoading() {
    this.isLoading = false;
    if (this.loadingElement) {
      this.loadingElement.style.display = 'none';
    }
    if (this.productGrid) {
      this.productGrid.classList.remove('loading');
    }
  }
  
  initModelDeduplication() {
    const productGrid = document.querySelector('.product-grid');
    const enableModelDedup = document.querySelector('#main-collection-product-grid')?.dataset.uniqueModels === 'true';
    const modelWordCount = parseInt(document.querySelector('#main-collection-product-grid')?.dataset.modelNameWordCount || '2');
    
    if (!enableModelDedup || !productGrid) return;
    
    // Apply model deduplication to initial products
    this.applyModelDeduplication(productGrid, modelWordCount);
    
    // Re-apply after filter changes
    document.addEventListener('collection:filtered', () => {
      this.applyModelDeduplication(productGrid, modelWordCount);
    });
  }
  
  applyModelDeduplication(container, wordCount) {
    const productElements = container.querySelectorAll('.card-product-wrapper');
    if (productElements.length === 0) return;
    
    const seenModels = new Set();
    const toRemove = [];
    
    productElements.forEach(el => {
      const productTitle = 
        el.getAttribute('data-product-title') || 
        el.querySelector('.card-product__title')?.textContent || 
        '';
      
      if (productTitle) {
        const modelName = this.extractModelName(productTitle, wordCount);
        
        if (modelName) {
          if (seenModels.has(modelName)) {
            toRemove.push(el);
          } else {
            seenModels.add(modelName);
          }
        }
      }
    });
    
    // Remove duplicates
    toRemove.forEach(el => el.remove());
    
    // Update count
    const remainingCount = productElements.length - toRemove.length;
    this.updateProductCount(remainingCount);
    
    console.log(`Model deduplication: removed ${toRemove.length} duplicates, ${remainingCount} products remaining`);
    
    // Auto-fill if deduplication reduced products below threshold
    if (toRemove.length > 0) {
      // Call the appropriate auto-fill method based on filtering mode
      if (this.filteringMode === 'native' && this.nativeFilters) {
        // For native mode, delegate to native filter enhancer
        this.nativeFilters.autoFillNativePageIfNeeded();
      } else {
        // For tag-based mode, use tag-based auto-fill
        this.autoFillPageIfNeeded();
      }
    }
  }

  applyModelDeduplicationToElements(productElements, wordCount) {
    if (!productElements || productElements.length === 0) return productElements;
    
    const seenModels = new Set();
    const filteredElements = [];
    
    productElements.forEach(el => {
      const productTitle = 
        el.getAttribute('data-product-title') || 
        el.querySelector('.card-product__title')?.textContent || 
        '';
      
      if (productTitle) {
        const modelName = this.extractModelName(productTitle, wordCount);
        
        if (modelName) {
          if (!seenModels.has(modelName)) {
            seenModels.add(modelName);
            filteredElements.push(el);
          }
        } else {
          filteredElements.push(el);
        }
      } else {
        filteredElements.push(el);
      }
    });
    
    console.log(`Model deduplication: filtered ${productElements.length - filteredElements.length} duplicates from ${productElements.length} products`);
    
    return filteredElements;
  }
  
  extractModelName(title, wordCount = 2) {
    if (!title) return '';
    
    const words = title.trim().split(/\s+/);
    
    // Special case: Check if title starts with "KOMONO x" (case-insensitive)
    // This handles collaborations like "KOMONO x Raf", "KOMONO x Tom", etc.
    if (words.length >= 3 && 
        words[0].toLowerCase() === 'komono' && 
        words[1].toLowerCase() === 'x') {
      
      // For "KOMONO x [Name] [Model]", we want to include:
      // - "KOMONO x [Name]" (first 3 words) + the model identifier (next wordCount words)
      // Example: "KOMONO x Raf Simons Alex Black" with wordCount=1 becomes "komono x raf alex"
      const collabPrefix = words.slice(0, 3); // "KOMONO x [CollaboratorName]"
      const modelWords = words.slice(3, 3 + wordCount); // Next wordCount words after the collab
      
      return [...collabPrefix, ...modelWords].join(' ').toLowerCase().trim();
    }
    
    // Standard case: Use first N words
    return words.slice(0, Math.min(wordCount, words.length)).join(' ').toLowerCase().trim();
  }
  
  initBusinessRules() {
    // Optical exclusions and other custom business logic
    document.addEventListener('collection:filtered', (e) => {
      this.applyBusinessRules(e.detail);
    });
  }
  
  applyBusinessRules(filterState) {
    // Example: Exclude opticals from availability filters
    if (filterState && filterState.availabilityFilter) {
      const productGrid = document.querySelector('.product-grid');
      const opticalProducts = productGrid.querySelectorAll('[data-product-type*="optical" i]');
      
      opticalProducts.forEach(product => {
        product.style.display = 'none';
      });
    }
    
    // Add more business rules as needed
  }

  clearAllFilters() {
    // Uncheck all checkboxes
    this.filterCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
      const label = checkbox.closest('.filter-option-label');
      if (label) label.classList.remove('active');
    });
    
    // Clear active filters and product type filters
    this.activeFilters.clear();
    this.productTypeFilters.clear();
    
    // Clear price filter
    this.isPriceFilterActive = false;
    
    // Reset price slider if it exists
    if (this.priceSlider && this.priceMinInput && this.priceMaxInput) {
      this.priceSlider.set([this.initialPriceRange.min, this.initialPriceRange.max]);
      this.priceMinInput.value = this.initialPriceRange.min;
      this.priceMaxInput.value = this.initialPriceRange.max;
      this.priceRange = { ...this.initialPriceRange };
    }
    
    this.updateActiveFiltersUI();
    this.updateURL();
    this.fetchFilteredProducts();
  }
  
  initUIEnhancements() {
    // Always ensure mobile filter toggle works regardless of mode
    const mobileFilterButtons = document.querySelectorAll('.filter-toggle-button');
    const filterClose = document.querySelector('.filter-close');
    const filterApply = document.querySelector('.filter-apply');
    
    console.log(`Found ${mobileFilterButtons.length} mobile filter buttons`);
    
    // Attach event to all filter toggle buttons (in case there are multiple)
    mobileFilterButtons.forEach((button, index) => {
      button.addEventListener('click', this.toggleMobileFilters.bind(this));
      console.log(`Mobile filter button ${index + 1} event listener attached`);
    });

    const filterClear = document.querySelector('.filter-clear');
    if (filterClear) {
      filterClear.addEventListener('click', () => {
        this.toggleMobileFilters();
        this.clearAllFilters();
      });
    }
    
    if (filterClose) {
      filterClose.addEventListener('click', this.toggleMobileFilters.bind(this));
    }
    
    if (filterApply) {
      filterApply.addEventListener('click', this.toggleMobileFilters.bind(this));
    }
    
    // Overlay click to close
    const overlay = document.querySelector('.content-overlay--left');
    if (overlay) {
      overlay.addEventListener('click', this.toggleMobileFilters.bind(this));
    }
  }
  
  toggleMobileFilters() {
    document.body.classList.toggle('filters-drawer-open');
    const filtersElement = document.querySelector('.collection-filters');
    console.log('Toggling mobile filters');
    if (filtersElement) {
      filtersElement.classList.toggle('open');
    }
  }
  
  updateProductCount(count) {
    const countElement = document.getElementById('CollectionProductCount');
    if (countElement) {
      countElement.textContent = count === 1 
        ? '1 product' 
        : `${count} products`;
    }
  }
  
  async autoFillPageIfNeeded() {
    // Configuration: minimum products to show (typically same as products per page)
    const minProductsPerPage = 48; // Adjust based on your collection settings
    const maxAutoFetchAttempts = 5; // Prevent infinite loops
    
    // Get current visible products
    const currentGrid = document.querySelector('.product-grid');
    if (!currentGrid) return;
    
    let visibleProducts = currentGrid.querySelectorAll('.card-product-wrapper').length;
    
    // Only auto-fill if we have fewer products than minimum and there are more pages
    if (visibleProducts >= minProductsPerPage || !this.hasMorePages || this.isLoadingMore) {
      return;
    }
    
    console.log(`Auto-filling: ${visibleProducts} products visible, need ${minProductsPerPage}`);
    
    let attempts = 0;
    
    while (visibleProducts < minProductsPerPage && this.hasMorePages && !this.isLoadingMore && attempts < maxAutoFetchAttempts) {
      attempts++;
      console.log(`Auto-fill attempt ${attempts}: Loading next page...`);
      
      // Load next page
      await this.loadTagBasedNextPage();
      
      // Check new count
      const newVisibleProducts = currentGrid.querySelectorAll('.card-product-wrapper').length;
      
      // If no new products were added, stop trying
      if (newVisibleProducts === visibleProducts) {
        console.log('Auto-fill: No new products added, stopping');
        break;
      }
      
      // Update count for next iteration
      visibleProducts = newVisibleProducts;
      
      // Small delay to prevent hammering the server
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check again if we have enough products now
      if (visibleProducts >= minProductsPerPage) {
        console.log(`Auto-fill complete: ${visibleProducts} products now visible`);
        break;
      }
    }
    
    if (attempts >= maxAutoFetchAttempts) {
      console.log('Auto-fill: Reached max attempts');
    }
    
    // Update product count after auto-fill completes
    const finalCount = currentGrid.querySelectorAll('.card-product-wrapper').length;
    this.updateProductCount(finalCount);
  }
  
  // Tag-based infinite scroll methods
  setupTagBasedInfiniteScroll() {
    // Create intersection observer for infinite scroll
    if (!this.infiniteScrollObserver) {
      this.infiniteScrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoadingMore && this.hasMorePages) {
            this.loadTagBasedNextPage();
          }
        });
      }, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      });
    }
    
    // Parse initial page data
    this.parseTagBasedInitialPageData();
    
    // Setup initial trigger element
    this.setupTagBasedScrollTrigger();
  }
  
  parseTagBasedInitialPageData() {
    // Parse page data from current URL and product count
    const url = new URL(window.location);
    const page = url.searchParams.get('page');
    this.currentPage = page ? parseInt(page) : 1;
    
    // Check if we have more pages by looking at pagination or total count
    const productCountElement = document.querySelector('#CollectionProductCount');
    const productGridElement = document.querySelector('.product-grid');
    const paginationElement = document.querySelector('.pagination');
    
    if (productCountElement && productGridElement) {
      // Extract total count from text like "127 products"
      const countText = productCountElement.textContent.trim();
      const totalProducts = parseInt(countText.replace(/\D/g, '')) || 0;
      const displayedProducts = productGridElement.querySelectorAll('.card-product-wrapper').length;
      
      this.totalProductCount = totalProducts;
      
      // Check if we have more pages either by count comparison or pagination presence
      this.hasMorePages = displayedProducts < totalProducts || 
                          (paginationElement && paginationElement.querySelector('a[aria-label="Next page"]'));
                          
      console.log('Tag-based infinite scroll state:', {
        totalProducts,
        displayedProducts,
        hasMorePages: this.hasMorePages,
        currentPage: this.currentPage
      });
    } else {
      this.hasMorePages = false;
    }
  }
  
  parseTagBasedPaginationData(tempDiv) {
    // Parse pagination info from fetched HTML
    const paginationElement = tempDiv.querySelector('.pagination');
    
    if (paginationElement) {
      const hasNextPage = paginationElement.querySelector('a[aria-label="Next page"]');
      this.hasMorePages = !!hasNextPage;
      console.log('Pagination element found, hasNextPage:', !!hasNextPage);
    } else {
      // Fallback: Compare total displayed products against total count
      const currentGrid = document.querySelector('.product-grid');
      const currentDisplayedProducts = currentGrid ? currentGrid.querySelectorAll('.card-product-wrapper').length : 0;
      
      // Check if we have the total count from initial page load
      if (this.totalProductCount > 0) {
        this.hasMorePages = currentDisplayedProducts < this.totalProductCount;
        console.log(`Comparing: ${currentDisplayedProducts} displayed vs ${this.totalProductCount} total`);
      } else {
        // If no total count, assume more pages if we got a full page of products
        const productsPerPage = 24; // Adjust based on your collection settings
        const newProducts = tempDiv.querySelectorAll('.card-product-wrapper').length;
        this.hasMorePages = newProducts >= productsPerPage;
        console.log(`No total count, checking if full page: ${newProducts} products`);
      }
    }
  }
  
  setupTagBasedScrollTrigger() {
    // Remove existing trigger if any
    const existingTrigger = document.querySelector('.infinite-scroll-trigger');
    if (existingTrigger) {
      this.infiniteScrollObserver.unobserve(existingTrigger);
      existingTrigger.remove();
    }
    
    // Only create trigger if we have more pages
    if (!this.hasMorePages) {
      console.log('No more pages to load');
      return;
    }
    
    // Find product grid to place trigger after it
    const productGrid = document.querySelector('.product-grid');
    
    if (productGrid) {
      // Create invisible trigger element after the product grid
      const trigger = document.createElement('div');
      trigger.className = 'infinite-scroll-trigger';
      trigger.style.height = '1px';
      trigger.style.width = '100%';
      trigger.style.opacity = '0';
      
      // Insert trigger after product grid
      productGrid.parentNode.insertBefore(trigger, productGrid.nextSibling);
      
      // Start observing
      this.infiniteScrollObserver.observe(trigger);
      console.log('Tag-based scroll trigger created and observed');
    }
  }
  
  async loadTagBasedNextPage() {
    if (this.isLoadingMore || !this.hasMorePages) {
      return;
    }
    
    this.isLoadingMore = true;
    const nextPage = this.currentPage + 1;
    
    console.log(`Loading tag-based page ${nextPage}...`);
    
    try {
      // Build URL for next page with current filters
      let url = `/collections/${this.collectionHandle}`;
      
      // Add tag-based filter parameters (same as fetchFilteredProducts)
      if (this.hasActiveFilters()) {
        const tagConstraints = [];
        
        this.activeFilters.forEach((values, category) => {
          if (category !== 'availability') {
            values.forEach(value => {
              value = value.replace(/\s+/g, '-').toLowerCase();
              if (category === 'shape') {
                const checkbox = document.querySelector(`.filter-checkbox[data-category="shape"][data-value="${value}"]`);
                if (checkbox && checkbox.dataset.originalValue) {
                  tagConstraints.push(`filter-${category}-${checkbox.dataset.originalValue}`);
                } else {
                  tagConstraints.push(`filter-${category}-${value}`);
                }
              } else {
                tagConstraints.push(`filter-${category}-${value}`);
              }
            });
          }
        });
        
        if (tagConstraints.length > 0) {
          url += `/${tagConstraints.join('+')}`;
        }
      }
      
      // Add view parameter for JSON response
      url += '?view=ajax';
      
      // Add page parameter
      url += `&page=${nextPage}`;
      
      // Add sort parameter if present (exclude 'manual' as it's Shopify's default)
      if (this.sortSelect?.value && this.sortSelect.value !== 'manual') {
        url += `&sort_by=${this.sortSelect.value}`;
      }
      
      // Show loading indicator
      this.showLoading();
      
      // Fetch next page
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const html = await response.text();
      
      // Parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Extract new products
      let newProducts = tempDiv.querySelectorAll('.card-product-wrapper');
      
      // Apply client-side filters
      const rawProductCount = newProducts.length;
      newProducts = this.applyClientSideFilters(Array.from(newProducts));
      
      console.log(`Raw products from page ${nextPage}: ${rawProductCount}, after filters: ${newProducts.length}`);
      
      if (newProducts.length > 0) {
        // Add new products to current grid
        const currentGrid = document.querySelector('.product-grid');
        
        newProducts.forEach((product) => {
          currentGrid.appendChild(product);
        });
        
        // Update current page
        this.currentPage = nextPage;
        
        // Parse pagination data to check for more pages (use rawProductCount for this)
        this.parseTagBasedPaginationData(tempDiv);
        
        // Setup new scroll trigger if there are more pages
        this.setupTagBasedScrollTrigger();
        
        console.log(`Loaded ${newProducts.length} products from page ${nextPage}, hasMorePages: ${this.hasMorePages}`);
        
        // Dispatch event for any code that needs to know about new products
        document.dispatchEvent(new CustomEvent('collection:products:loaded', {
          detail: { products: newProducts.length, page: this.currentPage }
        }));
      } else if (rawProductCount > 0) {
        // We got products but they were all filtered out client-side
        // Still update page and check for more
        this.currentPage = nextPage;
        this.parseTagBasedPaginationData(tempDiv);
        
        console.log(`Got ${rawProductCount} products but all filtered out, checking for more pages...`);
        
        // If there are more pages, continue loading
        if (this.hasMorePages) {
          this.setupTagBasedScrollTrigger();
        }
      } else {
        this.hasMorePages = false;
        console.log('No more products to load');
      }
      
    } catch (error) {
      console.error('Error loading next page:', error);
      this.hasMorePages = false; // Stop trying on error
    } finally {
      this.isLoadingMore = false;
      this.hideLoading();
    }
  }
}

/**
 * Enhanced Native Filtering
 * - Extends component-facets.js functionality with custom features
 */
class NativeFilterEnhancer {
  constructor() {
    this.facetForm = document.querySelector('facet-filters-form');
    this.automaticOpticalsExclusion = false; // Track if Opticals exclusion was added automatically
    this.infiniteScrollObserver = null;
    this.isLoadingMore = false;
    this.currentPage = 1;
    this.hasMorePages = true;
    
    console.log('Native filter enhancer initialized:', this.facetForm ? 'Found facet form' : 'No facet form found');
    
    this.bindEvents();
    this.initializeUI();
    this.initializeActiveStates();
    this.handleInitialPageState();
    this.parseInitialPageData();
    this.setupInfiniteScroll();
  }
  
  initializeUI() {
    // Initialize dropdown behavior for native filters
    const filterDropdowns = document.querySelectorAll('.filter-dropdown');
    
    filterDropdowns.forEach(dropdown => {
      const heading = dropdown.querySelector('.filter-dropdown-heading');
      const content = dropdown.querySelector('.filter-dropdown-content');
      const icon = heading?.querySelector('.filter-dropdown-icon svg');
      
      if (heading && content) {
        heading.addEventListener('click', () => {
          const isOpen = dropdown.classList.contains('open');
          
          // Close all other dropdowns (accordion behavior)
          filterDropdowns.forEach(other => {
            if (other !== dropdown) {
              other.classList.remove('open');
              const otherIcon = other.querySelector('.filter-dropdown-icon svg');
              if (otherIcon) {
                otherIcon.style.transform = 'rotate(0deg)';
              }
            }
          });
          
          // Toggle current dropdown
          dropdown.classList.toggle('open');
          
          // Rotate icon properly
          if (icon) {
            icon.style.transform = dropdown.classList.contains('open') ? 'rotate(90deg)' : 'rotate(0deg)';
          }
        });
      }
    });
  }
  
  bindEvents() {
    if (!this.facetForm) return;
    
    // Initialize timeout for batched submissions
    this.submitTimeout = null;
    
    // Listen for form submissions
    const form = this.facetForm.querySelector('form');
    if (form) {
      // Prevent default form submission and handle with AJAX
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        console.log('Native filter form submitted - handling with AJAX');
        this.handleNativeFilterSubmission(form);
      });
      
      form.addEventListener('change', (e) => {
        console.log('Native filter changed:', e.target.name, e.target.value);
        
        // Update active states immediately for visual feedback
        if (e.target.type === 'checkbox' || e.target.type === 'radio') {
          const label = e.target.closest('.filter-option-label');
          if (label) {
            if (e.target.checked) {
              label.classList.add('active');
            } else {
              label.classList.remove('active');
            }
          }
          // Update active filters display immediately
          this.updateNativeActiveFiltersUI();
        }
        
        // Auto-submit form for dynamic filtering
        if (e.target.type === 'checkbox' || e.target.type === 'radio') {
          // For checkboxes and radio buttons, auto-submit after a short delay
          clearTimeout(this.submitTimeout);
          this.submitTimeout = setTimeout(() => {
            this.handleNativeFilterSubmission(form);
          }, 300); // 300ms delay to allow multiple quick selections
        } else {
          // For other inputs (like price range), submit immediately
          this.handleNativeFilterSubmission(form);
        }
      });
    }
    
    // Handle clear filters button (now outside the form)
    const clearButton = document.querySelector('.filter-clear');
    if (clearButton) {
      clearButton.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Native filters cleared');
        
        // Clear all checkboxes
        const checkboxes = this.facetForm.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => checkbox.checked = false);
        
        // Clear all text inputs
        const textInputs = this.facetForm.querySelectorAll('input[type="text"], input[type="number"]');
        textInputs.forEach(input => input.value = '');
        
        // Reset automatic exclusion flags
        this.automaticOpticalsExclusion = false;
        
        // Update active filters display immediately
        this.updateNativeActiveFiltersUI();
        
        // Submit the cleared form
        this.handleNativeFilterSubmission(this.facetForm.querySelector('form'));
      });
    }
    
    // Handle apply filters button (now outside the form)
    const applyButton = document.querySelector('.filter-apply');
    if (applyButton) {
      applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Native filters apply button clicked');
        
        // Submit the form
        this.handleNativeFilterSubmission(this.facetForm.querySelector('form'));
      });
    }
    
    // Listen for native filter changes if component-facets.js is loaded
    this.facetForm.addEventListener('facet:updated', (e) => {
      // Trigger custom event for hybrid system
      document.dispatchEvent(new CustomEvent('collection:filtered', {
        detail: e.detail
      }));
    });
    
    // Enhance price range functionality
    this.enhancePriceRange();
  }
  
  enhancePriceRange() {
    const priceRangeElements = document.querySelectorAll('price-range');
    
    priceRangeElements.forEach(priceRange => {
      // Add custom price range enhancements if needed
      const inputs = priceRange.querySelectorAll('input[type="number"]');
      
      inputs.forEach(input => {
        input.addEventListener('change', () => {
          // Custom logic for price changes
          console.log('Price range updated:', input.name, input.value);
        });
      });
    });
  }
  
  handleNativeFilterSubmission(form) {
    // Get form data
    const formData = new FormData(form);
    const params = new URLSearchParams();
    
    // Convert form data to URL params
    for (const [key, value] of formData.entries()) {
      params.append(key, value);
    }
    
    // Check if price filtering is active and mark for client-side Opticals filtering
    const priceMinValue = formData.get('filter.v.price.gte');
    const priceMaxValue = formData.get('filter.v.price.lte');
    const isPriceFiltering = (priceMinValue && priceMinValue.trim()) || (priceMaxValue && priceMaxValue.trim());
    
    // Check if this is a mixed collection (has data attribute from template)
    const priceFilterGroup = document.querySelector('[data-filter-group="price"]');
    const hasMixedProducts = priceFilterGroup?.dataset?.hasMixedProducts === 'true';
    
    // If price filtering is active in a mixed collection, we'll filter out Opticals server-side
    // but only if the user hasn't manually set any product type filters
    if (isPriceFiltering && hasMixedProducts) {
      const hasManualProductTypeFilter = Array.from(formData.entries()).some(([key, value]) => 
        key === 'filter.p.product_type' || key === 'filter.p.type'
      );
      
      console.log('Price filtering detected:', {
        isPriceFiltering,
        hasMixedProducts,
        hasManualProductTypeFilter,
        priceMin: priceMinValue,
        priceMax: priceMaxValue
      });
      
      if (!hasManualProductTypeFilter) {
        // Add product type filters to exclude Opticals at the server level
        this.automaticOpticalsExclusion = true;
        
        // Add all product types except Opticals to the URL params
        // This way Shopify will exclude Opticals at the server level
        const nonOpticalsTypes = ['Watch', 'Sunglasses', 'Jewelry'];
        nonOpticalsTypes.forEach(type => {
          params.append('filter.p.product_type', type);
        });
        
        console.log('Added product type filters to exclude Opticals:', nonOpticalsTypes);
      } else {
        this.automaticOpticalsExclusion = false;
      }
    } else {
      this.automaticOpticalsExclusion = false;
      console.log('No price filtering or not mixed collection:', {
        isPriceFiltering,
        hasMixedProducts
      });
    }
    
    // Preserve sort order (exclude 'manual' as it's Shopify's default)
    const currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort && currentSort !== 'manual') {
      params.set('sort_by', currentSort);
    }
    
    // Build URL for AJAX request
    const baseUrl = window.location.pathname;
    const url = `${baseUrl}?${params.toString()}`;
    
    // Update browser URL without reload
    window.history.replaceState({}, '', url);
    
    // Fetch filtered results via AJAX (similar to tag-based system)
    this.fetchFilteredResults(url);
  }
  
  async fetchFilteredResults(url) {
    try {
      // Show loading state
      const productGrid = document.querySelector('.product-grid');
      const loadingElement = document.querySelector('.collection-loading');
      
      if (loadingElement) {
        loadingElement.style.display = 'block';
      }
      if (productGrid) {
        productGrid.style.opacity = '0.5';
      }
      
      // Fetch the filtered page
      const response = await fetch(url);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract the new product grid
      const newProductGrid = doc.querySelector('.product-grid');
      const newPagination = doc.querySelector('.pagination');
      const newResultsCount = doc.querySelector('#CollectionProductCount');
      const newActiveFilters = doc.querySelector('#active-filter-tags');
      const newSortWrapper = doc.querySelector('.collection__content--count-sort-wrapper');
      
      if (productGrid) {
        // Check if there are any products in the new grid
        const hasResults = newProductGrid && newProductGrid.children.length > 0 && !newProductGrid.querySelector('.collection-no-results');
        
        // Clear existing products except loading element (like tag-based mode)
        const loadingElement = productGrid.querySelector('.collection-loading');
        productGrid.innerHTML = '';
        if (loadingElement) {
          productGrid.appendChild(loadingElement);
        }
        
        if (hasResults) {
          // Add new products to grid (extract only the product elements)
          const newProducts = newProductGrid.querySelectorAll('.card-product-wrapper');
          
          newProducts.forEach(product => {
            productGrid.appendChild(product.cloneNode(true));
          });
        }
        
        if (!hasResults) {
          // Show no results message (similar to tag-based mode)
          const noResults = document.createElement('div');
          noResults.className = 'collection-no-results';
          noResults.innerHTML = `
            <h2 class="h3">No products found</h2>
            <p>Try adjusting your filters or <a class="link link-underline-start" href="${window.location.pathname}">view all products</a>.</p>
          `;
          productGrid.appendChild(noResults);
        }
        
        productGrid.style.opacity = '1';
        
        // Use the server's count from the response instead of just visible products
        const productCount = newResultsCount ? newResultsCount.textContent.trim() : '0 products';
        
        // Update product count with server response (preserves total count for pagination)
        const countElement = document.getElementById('CollectionProductCount');
        if (countElement && newResultsCount) {
          countElement.textContent = productCount;
        } else if (countElement && !hasResults) {
          countElement.textContent = '0 products';
        }
        
        // Update pagination if it exists
        const currentPagination = document.querySelector('.pagination');
        if (hasResults && newPagination && currentPagination) {
          currentPagination.innerHTML = newPagination.innerHTML;
        } else if (currentPagination) {
          // Hide pagination if no results
          currentPagination.innerHTML = '';
        }
        
        // Update active filters display
        const currentActiveFilters = document.querySelector('#active-filter-tags');
        if (newActiveFilters && currentActiveFilters) {
          currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
        } else if (currentActiveFilters) {
          // If no active filters in response, generate them from current form state
          this.updateNativeActiveFiltersUI();
        }
        
        // Update active states on filter checkboxes
        this.updateNativeFilterActiveStates();
        this.updateNativeActiveFiltersUI();
        
        // Extract total count for event dispatch
        const totalCountFromServer = newResultsCount ? parseInt(newResultsCount.textContent.replace(/\D/g, '')) || 0 : 0;
        
        // Trigger custom event for other scripts
        document.dispatchEvent(new CustomEvent('collection:filtered', {
          detail: {
            url: url,
            mode: 'native-ajax',
            hasResults: hasResults,
            count: totalCountFromServer,
            displayedCount: hasResults ? productGrid.querySelectorAll('.card-product-wrapper').length : 0
          }
        }));
        
        // Reset pagination state and setup infinite scroll for new results
        this.currentPage = 1;
        this.parseInitialPageData();
        
        // Always setup scroll trigger after filtering (parseInitialPageData sets hasMorePages correctly)
        setTimeout(() => {
          this.setupScrollTrigger();
          
          // Auto-fill page if filtered results are below threshold
          if (hasResults) {
            this.autoFillNativePageIfNeeded();
          }
        }, 100);
      }
      
      // Hide loading state
      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
      
    } catch (error) {
      console.error('Error fetching filtered results:', error);
      
      // Hide loading state
      const loadingElement = document.querySelector('.collection-loading');
      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
      
      // Fallback to normal form submission
      window.location.href = url;
    }
  }
  
  updateNativeFilterActiveStates() {
    // Get all checkboxes in the native form
    const checkboxes = this.facetForm.querySelectorAll('input[type="checkbox"]');
    
    checkboxes.forEach(checkbox => {
      const label = checkbox.closest('.filter-option-label');
      if (label) {
        if (checkbox.checked) {
          label.classList.add('active');
        } else {
          label.classList.remove('active');
        }
      }
    });
  }
  
  initializeActiveStates() {
    // Set initial active states when page loads
    this.updateNativeFilterActiveStates();
    this.updateNativeActiveFiltersUI();
  }
  
  handleInitialPageState() {
    console.log('Checking initial page state...');
    
    // Check if URL has filter parameters (indicating this should be a filtered state)
    const urlHasFilters = window.location.search.includes('filter') || window.location.pathname.includes('+');
    
    if (!urlHasFilters) {
      console.log('No filters in URL, skipping fix');
      return; // No filters in URL, so this is just a genuinely empty collection
    }

    console.log('URL has filters, checking for broken state...');
    
    // Check for the Shopify-rendered empty state that should actually show filters
    const collectionEmpty = document.querySelector('.collection-empty');
    const collectionGrid = document.querySelector('.collection-grid');
    
    if (collectionEmpty && collectionGrid) {
      console.log('Found collection-empty with filters in URL - replacing with filtered layout');
      
      // Get collection handle for proper filter loading
      const collectionHandle = collectionGrid.dataset.collectionHandle || 'default';
      
      // Replace the entire collection-grid content with the proper structure
      collectionGrid.innerHTML = `
        <div class="collection-header"></div>
        <div class="collection-content">
          <div class="collection-sidebar">
            <div class="collection-filters" data-collection-handle="${collectionHandle}" data-filtering-mode="native">
              <div class="filter-mobile-header">
                <h2 class="filter-title h3">Filters</h2>
                <button class="filter-close" aria-label="Close filters">
                  <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                    <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="inherit"></path>
                  </svg>
                </button>
              </div>
              <p>Loading filters...</p>
            </div>
            <div class="active-filters">
              <div id="active-filter-tags" class="active-filter-group"></div>
              <button id="clear-all-filters" class="clear-all-filters link link-underline-start" style="display: none;">
                Clear all filters
              </button>
            </div>
            <div class="collection__content--count-sort-wrapper">
              <div id="CollectionProductCount" class="h5" role="status">0 products</div>
              <div class="collection-sort-container">
                <div class="collection-sort link link-underline-start">
                  <label for="SortBy" class="sort-label">Sort by:</label>
                  <div class="select-wrapper">
                    <select name="sort_by" id="SortBy" class="sort-select">
                      <option value="manual">Featured</option>
                      <option value="best-selling">Best selling</option>
                      <option value="title-ascending">Alphabetically, A-Z</option>
                      <option value="title-descending">Alphabetically, Z-A</option>
                      <option value="price-ascending">Price, low to high</option>
                      <option value="price-descending">Price, high to low</option>
                      <option value="created-ascending">Date, old to new</option>
                      <option value="created-descending">Date, new to old</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="collection-products">
            <div id="CollectionProductGrid" class="product-grid">
              <div class="collection-no-results">
                <h2 class="h3">No products found</h2>
                <p>Try adjusting your filters or <a class="link link-underline-start" href="${window.location.pathname}">view all products</a>.</p>
              </div>
            </div>
          </div>
        </div>
      `;
      
      // Now load the actual filters for this collection
      this.loadFiltersForCollection(collectionHandle);
      
      console.log('Successfully replaced empty state with filtered layout');
    } else {
      console.log('No collection-empty found, checking for other cases...');
      
      // Handle case where we have product grid but need no-results message
      const productGrid = document.querySelector('.product-grid');
      if (productGrid) {
        const productWrappers = productGrid.querySelectorAll('.card-product-wrapper');
        const hasProducts = productWrappers.length > 0;
        const hasNoResultsMessage = productGrid.querySelector('.collection-no-results');
        
        if (!hasProducts && !hasNoResultsMessage) {
          console.log('Adding no-results message to empty product grid');
          
          const loadingElement = productGrid.querySelector('.collection-loading');
          productGrid.innerHTML = '';
          if (loadingElement) {
            productGrid.appendChild(loadingElement);
          }
          
          const noResults = document.createElement('div');
          noResults.className = 'collection-no-results';
          noResults.innerHTML = `
            <h2 class="h3">No products found</h2>
            <p>Try adjusting your filters or <a class="link link-underline-start" href="${window.location.pathname}">view all products</a>.</p>
          `;
          productGrid.appendChild(noResults);
          
          const countElement = document.getElementById('CollectionProductCount');
          if (countElement) {
            countElement.textContent = '0 products';
          }
        }
      }
    }
  }
  
  async loadFiltersForCollection(collectionHandle) {
    console.log(`Loading filters for collection: ${collectionHandle}`);
    
    try {
      // Make AJAX request to get the collection page with full filter structure
      const response = await fetch(`/collections/${collectionHandle}`, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract the filter content from the full page
        const sourceFiltersElement = doc.querySelector('.collection-filters');
        const targetFiltersContainer = document.querySelector('.collection-filters');
        
        if (sourceFiltersElement && targetFiltersContainer) {
          console.log('Replacing filter content...');
          
          // Replace the content
          targetFiltersContainer.innerHTML = sourceFiltersElement.innerHTML;
          
          // Reinitialize the filter system
          setTimeout(() => {
            this.init();
            
            // Apply filters from URL after initialization
            this.applyFiltersFromURL();
            
            console.log('Successfully loaded and initialized filters');
          }, 100);
        } else {
          console.log('Could not find filter elements, falling back to redirect');
          // Fallback: redirect to collection page to get full structure
          window.location.href = `/collections/${collectionHandle}`;
        }
      } else {
        console.log('Filter loading request failed, redirecting...');
        // Fallback: redirect to collection page
        window.location.href = `/collections/${collectionHandle}`;
      }
    } catch (error) {
      console.error('Error loading filters:', error);
      // Fallback: redirect to collection page
      window.location.href = `/collections/${collectionHandle}`;
    }
  }
  
  applyFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filter.')) {
        // Handle checkbox filters
        const checkbox = document.querySelector(`input[name="${key}"][value="${value}"]`);
        if (checkbox) {
          checkbox.checked = true;
          console.log(`Applied filter from URL: ${key} = ${value}`);
        } else {
          // Handle price range filters
          const priceInput = document.querySelector(`input[name="${key}"]`);
          if (priceInput && priceInput.type === 'number') {
            priceInput.value = value;
            console.log(`Applied price filter from URL: ${key} = ${value}`);
          }
        }
      }
    }
    
    // Update active filters display
    setTimeout(() => {
      this.updateNativeActiveFiltersUI();
    }, 100);
  }
  
  updateNativeActiveFiltersUI() {
    const activeFiltersContainer = document.querySelector('#active-filter-tags');
    if (!activeFiltersContainer) return;
    
    // Clear existing active filters
    activeFiltersContainer.innerHTML = '';
    
    // Get all checked checkboxes in the native form
    const checkedBoxes = this.facetForm.querySelectorAll('input[type="checkbox"]:checked');
    
    // Get price range inputs
    const priceMinInput = this.facetForm.querySelector('input[name*="price.gte"]');
    const priceMaxInput = this.facetForm.querySelector('input[name*="price.lte"]');
    
    // Create a fragment for better performance
    const fragment = document.createDocumentFragment();
    
    // Handle checkbox filters
    checkedBoxes.forEach(checkbox => {
      // Skip automatic product type filters that were added due to price filtering
      const isProductTypeFilter = checkbox.name === 'filter.p.product_type' || checkbox.name === 'filter.p.type';
      if (isProductTypeFilter && this.automaticOpticalsExclusion) {
        // Don't show automatically added product type filters in active facets
        return;
      }
      
      const filterTag = document.createElement('a');
      filterTag.className = 'active-filter-tag';
      filterTag.classList.add('button', 'button--secondary');
      filterTag.href = 'javascript:void(0)';
      
      // Get filter information
      const filterGroup = checkbox.closest('.filter-group');
      const filterLabel = filterGroup?.querySelector('.filter-dropdown-heading .h4')?.textContent || 'Filter';
      const optionText = checkbox.closest('.filter-option-label')?.querySelector('.filter-option-text')?.textContent || checkbox.value;
      
      filterTag.innerHTML = `
        ${filterLabel}: ${optionText}
        <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
          <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="inherit"></path>
        </svg>
      `;
      
      // Add click handler to remove filter
      filterTag.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Uncheck the checkbox
        checkbox.checked = false;
        
        // Update active state
        const label = checkbox.closest('.filter-option-label');
        if (label) {
          label.classList.remove('active');
        }
        
        // If this was a manual product type filter, clear the automatic exclusion flags
        if (isProductTypeFilter) {
          this.automaticOpticalsExclusion = false;
        }
        
        // Submit form to update results
        this.handleNativeFilterSubmission(this.facetForm.querySelector('form'));
      });
      
      fragment.appendChild(filterTag);
    });
    
    // Handle price range filter
    if (priceMinInput || priceMaxInput) {
      const minValue = priceMinInput?.value?.trim();
      const maxValue = priceMaxInput?.value?.trim();
      
      if (minValue || maxValue) {
        const currency = document.querySelector('.field-currency')?.textContent || '€';
        let priceText = 'Price: ';
        
        if (minValue && maxValue) {
          priceText += `${currency}${minValue} - ${currency}${maxValue}`;
        } else if (minValue) {
          priceText += `over ${currency}${minValue}`;
        } else if (maxValue) {
          priceText += `under ${currency}${maxValue}`;
        }
        
        const filterTag = document.createElement('a');
        filterTag.className = 'active-filter-tag';
        filterTag.classList.add('button', 'button--secondary');
        filterTag.href = 'javascript:void(0)';
        
        filterTag.innerHTML = `
          ${priceText}
          <svg class="icon icon-close" xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="currentColor">
            <path d="M0.852344 1.14688L0.499219 0.793751L-0.207031 1.5L0.146094 1.85313L6.29297 8L0.146094 14.1469L-0.207031 14.5L0.499219 15.2063L0.852344 14.8531L6.99922 8.70625L13.1461 14.8531L13.4992 15.2063L14.2055 14.5L13.8523 14.1469L7.70547 8L13.8523 1.85313L14.2055 1.5L13.4992 0.793751L13.1461 1.14688L6.99922 7.29375L0.852344 1.14688Z" fill="inherit"></path>
          </svg>
        `;
        
        // Add click handler to clear price filter
        filterTag.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Clear price inputs
          if (priceMinInput) priceMinInput.value = '';
          if (priceMaxInput) priceMaxInput.value = '';
          
          // Submit form to update results
          this.handleNativeFilterSubmission(this.facetForm.querySelector('form'));
        });
        
        fragment.appendChild(filterTag);
      }
    }
    
    // Add all filter tags to the container
    // Add all filter tags to the container
    activeFiltersContainer.appendChild(fragment);
    
    // Show/hide clear all button
    const clearAllButton = document.getElementById('clear-all-filters');
    if (clearAllButton) {
      clearAllButton.style.display = (checkedBoxes.length > 0 || (priceMinInput?.value?.trim()) || (priceMaxInput?.value?.trim())) ? 'block' : 'none';
    }
  }
  
  setupInfiniteScroll() {
    // Create intersection observer for infinite scroll
    this.infiniteScrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoadingMore && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    });
    
    // Setup initial trigger element at bottom of product grid
    this.setupScrollTrigger();
  }
  
  parseInitialPageData() {
    // Parse page data from current URL and product count
    const url = new URL(window.location);
    const page = url.searchParams.get('page');
    this.currentPage = page ? parseInt(page) : 1;
    
    // Check if we have more pages by looking at pagination or total count
    const productCountElement = document.querySelector('#CollectionProductCount');
    const productGridElement = document.querySelector('.product-grid');
    const paginationElement = document.querySelector('.pagination');
    
    if (productCountElement && productGridElement) {
      // Extract total count from text like "127 products"
      const countText = productCountElement.textContent.trim();
      const totalProducts = parseInt(countText.replace(/\D/g, '')) || 0;
      const displayedProducts = productGridElement.querySelectorAll('.card-product-wrapper').length;
      
      // Check if we have more pages either by count comparison or pagination presence
      this.hasMorePages = displayedProducts < totalProducts || 
                          (paginationElement && paginationElement.querySelector('a[aria-label="Next page"]'));
                          
      console.log('Native infinite scroll state:', {
        totalProducts,
        displayedProducts,
        hasMorePages: this.hasMorePages,
        currentPage: this.currentPage
      });
    } else {
      this.hasMorePages = false;
    }
  }
  
  setupScrollTrigger() {
    // Remove existing trigger if any
    const existingTrigger = document.querySelector('.infinite-scroll-trigger');
    if (existingTrigger) {
      this.infiniteScrollObserver.unobserve(existingTrigger);
      existingTrigger.remove();
    }
    
    // Only create trigger if we have more pages
    if (!this.hasMorePages) {
      return;
    }
    
    // Find product grid to place trigger after it
    const productGrid = document.querySelector('.product-grid');
    
    if (productGrid) {
      // Create invisible trigger element after the product grid
      const trigger = document.createElement('div');
      trigger.className = 'infinite-scroll-trigger';
      trigger.style.height = '1px';
      trigger.style.width = '100%';
      trigger.style.opacity = '0';
      
      // Insert trigger after product grid
      productGrid.parentNode.insertBefore(trigger, productGrid.nextSibling);
      
      // Start observing
      this.infiniteScrollObserver.observe(trigger);
    }
  }
  
  async loadNextPage() {
    if (this.isLoadingMore || !this.hasMorePages) {
      return;
    }
    
    this.isLoadingMore = true;
    const nextPage = this.currentPage + 1;
    
    try {
      // Build URL for next page with current filters
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('page', nextPage.toString());
      const nextPageUrl = currentUrl.toString();
      
      // Show loading indicator (same as tag-based mode)
      this.showNativeLoadingForInfiniteScroll();
      
      // Fetch next page
      const response = await fetch(nextPageUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract new products
      const newProducts = doc.querySelectorAll('.card-product-wrapper');
      
      if (newProducts.length > 0) {
        // Add new products to current grid
        const currentGrid = document.querySelector('.product-grid');
        
        newProducts.forEach((product) => {
          const clonedProduct = product.cloneNode(true);
          currentGrid.appendChild(clonedProduct);
        });
        
        // Update current page
        this.currentPage = nextPage;
        
        // Check if there are more pages by comparing product counts
        const newProductCount = doc.querySelector('#CollectionProductCount');
        const totalProducts = newProductCount ? parseInt(newProductCount.textContent.replace(/\D/g, '')) || 0 : 0;
        const currentDisplayed = document.querySelectorAll('.product-grid .card-product-wrapper').length;
        this.hasMorePages = currentDisplayed < totalProducts;
        
        // Setup new scroll trigger if there are more pages
        this.setupScrollTrigger();
        
        // Dispatch event for any code that needs to know about new products
        document.dispatchEvent(new CustomEvent('collection:products:loaded', {
          detail: { products: newProducts.length, page: this.currentPage }
        }));
      } else {
        this.hasMorePages = false;
      }
      
    } catch (error) {
      this.hasMorePages = false; // Stop trying on error
    } finally {
      this.isLoadingMore = false;
      this.hideNativeLoadingForInfiniteScroll();
    }
  }
  
  showNativeLoadingForInfiniteScroll() {
    // Use the same loading pattern as tag-based mode for infinite scroll
    const loadingElement = document.querySelector('.collection-loading');
    const productGrid = document.querySelector('.product-grid');
    
    if (loadingElement) {
      loadingElement.style.display = 'block';
    }
    if (productGrid) {
      productGrid.classList.add('loading');
    }
  }
  
  hideNativeLoadingForInfiniteScroll() {
    // Hide loading using the same pattern as tag-based mode
    const loadingElement = document.querySelector('.collection-loading');
    const productGrid = document.querySelector('.product-grid');
    
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
    if (productGrid) {
      productGrid.classList.remove('loading');
    }
  }
  
  async autoFillNativePageIfNeeded() {
    // Configuration: minimum products to show (typically same as products per page)
    const minProductsPerPage = 48; // Adjust based on your collection settings
    const maxAutoFetchAttempts = 5; // Prevent infinite loops
    
    // Get current visible products
    const currentGrid = document.querySelector('.product-grid');
    if (!currentGrid) return;
    
    let visibleProducts = currentGrid.querySelectorAll('.card-product-wrapper').length;
    
    // Only auto-fill if we have fewer products than minimum and there are more pages
    if (visibleProducts >= minProductsPerPage || !this.hasMorePages || this.isLoadingMore) {
      return;
    }
    
    console.log(`Native auto-filling: ${visibleProducts} products visible, need ${minProductsPerPage}`);
    
    let attempts = 0;
    
    while (visibleProducts < minProductsPerPage && this.hasMorePages && !this.isLoadingMore && attempts < maxAutoFetchAttempts) {
      attempts++;
      console.log(`Native auto-fill attempt ${attempts}: Loading next page...`);
      
      // Load next page
      await this.loadNextPage();
      
      // Check new count
      const newVisibleProducts = currentGrid.querySelectorAll('.card-product-wrapper').length;
      
      // If no new products were added, stop trying
      if (newVisibleProducts === visibleProducts) {
        console.log('Native auto-fill: No new products added, stopping');
        break;
      }
      
      // Update count for next iteration
      visibleProducts = newVisibleProducts;
      
      // Small delay to prevent hammering the server
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check again if we have enough products now
      if (visibleProducts >= minProductsPerPage) {
        console.log(`Native auto-fill complete: ${visibleProducts} products now visible`);
        break;
      }
    }
    
    if (attempts >= maxAutoFetchAttempts) {
      console.log('Native auto-fill: Reached max attempts');
    }
    
    // Update product count after auto-fill completes
    const finalCount = currentGrid.querySelectorAll('.card-product-wrapper').length;
    const countElement = document.getElementById('CollectionProductCount');
    if (countElement) {
      countElement.textContent = finalCount === 1 
        ? '1 product' 
        : `${finalCount} products`;
    }
  }
}

/**
 * Compatibility Layer
 * - Ensures existing functionality continues to work
 */
class FilterCompatibilityLayer {
  constructor() {
    // Map old events to new hybrid events
    this.mapLegacyEvents();
  }
  
  mapLegacyEvents() {
    // If legacy code listens for old events, map them to new ones
    document.addEventListener('collection:products:updated', (e) => {
      document.dispatchEvent(new CustomEvent('collection:filtered', {
        detail: e.detail
      }));
    });
  }
}

// Initialize the hybrid system
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on a collection page with hybrid filtering
  const hybridFilters = document.querySelector('.collection-filters[data-filtering-mode]');
  
  if (hybridFilters) {
    console.log('Initializing hybrid filtering system...');
    
    // Prevent any legacy collection filters from initializing
    window.hybridFilteringActive = true;
    
    // Initialize hybrid system
    window.hybridFilters = new HybridCollectionFilters();
    
    console.log('Hybrid filtering system fully initialized');
  }
});

// Prevent legacy CollectionFilters from initializing if hybrid is active
document.addEventListener('DOMContentLoaded', () => {
  if (window.hybridFilteringActive) {
    // Override legacy initialization
    window.CollectionFilters = function() {
      console.log('Legacy CollectionFilters blocked - using hybrid system instead');
    };
  }
});

// Initialize compatibility layer
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.collection-filters')) {
    new FilterCompatibilityLayer();
  }
});

// Export for potential external use
window.HybridCollectionFilters = HybridCollectionFilters;